import { createContext, useContext, type Context } from 'react';

import type { AppChromeProps } from '../common/types';
import type { AppChrome$data } from '../ui/__generated__/AppChrome.graphql';

/**
 * The `AppChrome`-scoped values the base navigation components render with.
 *
 * These are passed through context rather than captured in a closure so that
 * the base TopNav/SideNav components can be declared at module scope — see the
 * note on `BaseSideNav` for why their identity must be stable.
 */
export interface InternalAppChromeContextType extends Pick<
	AppChromeProps,
	| 'app'
	| 'appLabel'
	| 'closeChatPanel'
	| 'closeHelpPanel'
	| 'customizationStore'
	| 'intlMode'
	| 'isAnonymous'
	| 'isChatPanelOpen'
	| 'isHelpPanelOpen'
	| 'networkStorageParams'
	| 'openChatPanel'
	| 'openHelpPanel'
> {
	/**
	 * The unmasked `AppChrome` fragment reference that GTN and GSN read their
	 * navigation data from. `null`/`undefined` when no navigation data has been
	 * loaded (e.g. the default no-op `useRelayQuery`).
	 */
	appChromeData: AppChrome$data | null | undefined;
}

export const InternalAppChromeContext: Context<InternalAppChromeContextType | null> =
	createContext<InternalAppChromeContextType | null>(null);

export function useInternalAppChromeContext(componentName: string): InternalAppChromeContextType {
	const value = useContext(InternalAppChromeContext);

	if (value === null) {
		throw new Error(
			`${componentName} is only valid inside AppChrome. It is handed to a slot as its base component and must be rendered from there, not exported or re-parented.`,
		);
	}

	return value;
}
